import cn from 'classnames';
import { type ReactNode } from 'react';

import { useDecisions, useDeviceType } from '@libs/common-next';

import { useProduct } from '@/entities/product';
import { usePurchaseFormState, usePurchaseFormWatch } from '@/entities/purchase-form';
import { useSoftLoginGuard } from '@/entities/session';
import { useAutoshipCustomerStatus } from '@/features/autoship-customer-status';
import { SetUpAutoshipButton } from '@/features/fulfilment-selection';

import { useNearbyStoresWithInventory, useOptimizelyPurchaseCTAObserver } from '../../model';
import { OptimizelyStickyProductDescription } from '../OptimizelyStickyProductDescription';
import { PersonalizationFields } from '../personalization-fields';
import { AddToCartButton } from './AddToCartButton';
import { AutoshipButton } from './AutoshipButton';
import { QuantitySelector } from './QuantitySelector';
import { ShowInStockStoresButton } from './ShowInStockStoresButton';
import { ViewOrderButton } from './ViewOrderButton';

export const PurchaseCTA = () => {
    const autoshipFlag = useDecisions('ff_autoship');
    const desktopStickyFlag = useDecisions('w_sticky_cta');

    const { product } = useProduct();
    const { deviceType } = useDeviceType();
    const isDesktopStickyScroll =
        desktopStickyFlag === 'scroll' && deviceType === 'desktop' && !product?.personalization;
    const { hasPassed, sentinelRef } = useOptimizelyPurchaseCTAObserver(isDesktopStickyScroll);
    const { shouldRenderNearbyStoresCTA } = useNearbyStoresWithInventory();

    const { isSubmitSuccessful } = usePurchaseFormState();
    const purchaseOption = usePurchaseFormWatch('purchaseOption');
    const isAutoshipSelected = purchaseOption === 'autoship';

    const { isExistingAutoshipCustomer } = useAutoshipCustomerStatus();
    const { authGuard } = useSoftLoginGuard();

    const renderStickyCTA = (children: ReactNode) => (
        <>
            <div
                className={cn('optimizely-sticky-atc__wrapper', {
                    'optimizely-sticky-atc__wrapper--desktop-sticky': isDesktopStickyScroll && hasPassed,
                })}
            >
                {isDesktopStickyScroll && hasPassed && <OptimizelyStickyProductDescription />}
                {children}
            </div>
            {isDesktopStickyScroll && <div ref={sentinelRef} aria-hidden="true" />}
        </>
    );

    // BOPIS CTA - Current store is OOS but nearby stores have inventory
    if (shouldRenderNearbyStoresCTA) {
        return renderStickyCTA(<ShowInStockStoresButton />);
    }

    // Autoship CTA
    if (isAutoshipSelected && isExistingAutoshipCustomer && !product?.pharmacy) {
        if (isSubmitSuccessful) {
            return renderStickyCTA(<ViewOrderButton />);
        }

        return (
            <>
                <QuantitySelector />
                {renderStickyCTA(
                    autoshipFlag === 'crawl' ? (
                        <SetUpAutoshipButton onAuthRequired={authGuard} />
                    ) : (
                        <AutoshipButton onAuthRequired={authGuard} />
                    )
                )}
            </>
        );
    }

    // Personalization CTA
    if (product?.personalization) {
        return (
            <>
                <QuantitySelector />
                <PersonalizationFields renderSubmitButton={<AddToCartButton />} />
            </>
        );
    }

    // Default Add to Cart CTA
    return (
        <>
            <QuantitySelector />
            {renderStickyCTA(<AddToCartButton />)}
        </>
    );
};
